import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const LAST_UPDATED = "April 24, 2026";

export default function PrivacyPolicyScreen() {
  const nav = useNavigate();
  return (
    <div
      className="relative w-full h-full arcade-bg overflow-auto"
      data-testid="privacy-screen"
    >
      <div className="sticky top-0 z-20 backdrop-blur-md bg-[#0B0C10]/60 border-b border-white/5 safe-top">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-4 py-3">
          <button
            className="hud-pill hud-pill--cyan"
            onClick={() => nav(-1)}
            data-testid="privacy-back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-[#00FFFF]" />
            <span className="text-[#00FFFF] font-display text-sm">Back</span>
          </button>
          <h1 className="font-display font-black text-xl sm:text-2xl neon-cyan">
            PRIVACY POLICY
          </h1>
          <div className="w-20" />
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-5 sm:p-8 font-body text-white/80 leading-relaxed space-y-5">
        <p className="text-xs uppercase tracking-wider text-white/50">
          Last updated: {LAST_UPDATED}
        </p>

        <p>
          Crossbeau Chaos (&quot;the app&quot;) is a single-player offline
          arcade game. This policy explains what information the app handles
          and what it doesn&apos;t.
        </p>

        <Section title="What we collect">
          <p>
            <strong>Nothing personally identifying.</strong> The app does not
            ask for a name, email, phone number, location, or account. There
            is no login, no profile, and no user tracking tied to your
            identity.
          </p>
        </Section>

        <Section title="Local storage">
          <p>
            Game progress — coins, unlocked items, equipped gear, streaks,
            mini-game wins — is saved in <em>your device&apos;s local storage</em>
            (browser <code>localStorage</code> on the web, WebView storage on
            iOS and Android). This data never leaves your device. You can wipe
            it at any time from the Home screen via the reset button.
          </p>
        </Section>

        <Section title="No analytics or third-party tracking">
          <p>
            The app does not contain advertising, analytics, tracking pixels,
            fingerprinting, or third-party SDKs that collect information about
            you or your device.
          </p>
        </Section>

        <Section title="Network usage">
          <p>
            The game runs offline after initial load. The only network
            requests it makes are for loading its own assets (images, fonts)
            the first time you open it.
          </p>
        </Section>

        <Section title="Children">
          <p>
            Crossbeau Chaos is designed to be family-friendly. Because the app
            doesn&apos;t collect personal information from anyone, it also
            doesn&apos;t collect personal information from children. Parents
            and guardians can reset or delete all local progress via the Home
            screen&apos;s reset button at any time.
          </p>
        </Section>

        <Section title="Sharing">
          <p>
            If you use the <em>Share Score</em> feature, the app generates a
            scorecard image on your device and hands it to your operating
            system&apos;s native share sheet (or clipboard on the web). The
            app itself never uploads this image anywhere.
          </p>
        </Section>

        <Section title="Changes to this policy">
          <p>
            If this policy changes, the &quot;Last updated&quot; date above
            will change. Because the app does not communicate with any server,
            updates are delivered through new app versions.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Questions? Reach out via the store listing where you downloaded
            the app.
          </p>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-2">
      <h2 className="font-display font-black text-lg neon-pink tracking-wider">
        {title}
      </h2>
      <div className="text-white/80">{children}</div>
    </section>
  );
}
